import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  PaperAirplaneIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';
import { Spinner } from './ui';
import QuestionChart from './QuestionChart';
import type {
  Questionnaire, GeneratedQuestion, QuestionnaireResponsesData,
} from '../services/pracmagicTypes';

interface Props {
  clientId: string;
  coachClientId?: string;
}

type QuestionType = 'rating' | 'yesno' | 'freetext' | 'number';
type Frequency = 'weekly' | 'biweekly' | 'monthly' | 'once';

interface DraftQuestion {
  text: string;
  type: QuestionType;
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
});

const FREQ_LABELS: Record<Frequency, string> = {
  weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly', once: 'One-time',
};

const TYPE_LABELS: Record<QuestionType, string> = {
  rating: '1–5 Rating', yesno: 'Yes / No', freetext: 'Free text', number: 'Number',
};

// ─── Question type badge ──────────────────────────────────────────────────────

const TypeBadge = ({ type }: { type: QuestionType }) => {
  const colors: Record<QuestionType, string> = {
    rating:   'bg-blue-100 text-blue-700',
    yesno:    'bg-green-100 text-green-700',
    freetext: 'bg-purple-100 text-purple-700',
    number:   'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[type]}`}>
      {TYPE_LABELS[type]}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const QuestionnairesTab: React.FC<Props> = ({ clientId, coachClientId }) => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [chartView, setChartView] = useState<QuestionnaireResponsesData | null>(null);
  const [chartLoading, setChartLoading] = useState(false);

  const fetchQuestionnaires = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.questionnaires.byClient(clientId)}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      setQuestionnaires((data.questionnaires || []).filter((q: Questionnaire) => q.source !== 'form'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchQuestionnaires(); }, [fetchQuestionnaires]);

  const toggleActive = async (q: Questionnaire) => {
    await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.update(q.id)}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ is_active: !q.is_active }),
    });
    fetchQuestionnaires();
  };

  const deleteQuestionnaire = async (id: string) => {
    if (!window.confirm('Delete this check-in and all responses?')) return;
    await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.delete(id)}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    fetchQuestionnaires();
    if (chartView?.questionnaire.id === id) setChartView(null);
  };

  const sendReminder = async (id: string) => {
    await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.sendReminder(id)}`, {
      method: 'POST', headers: authHeaders(),
    });
    alert('Reminder sent!');
  };

  const openCharts = async (q: Questionnaire) => {
    setChartLoading(true);
    const res = await fetch(
      `${config.apiBaseUrl}${endpoints.main.questionnaires.responses(q.id)}`,
      { headers: authHeaders() }
    );
    const data: QuestionnaireResponsesData = await res.json();
    setChartView(data);
    setChartLoading(false);
  };

  // ── Chart view ──────────────────────────────────────────────────────────────
  if (chartView) {
    return (
      <div className="p-4">
        <button
          onClick={() => setChartView(null)}
          className="flex items-center gap-1.5 text-sm text-primary-600 hover:underline mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </button>
        <h3 className="font-semibold text-white mb-1">{chartView.questionnaire.title}</h3>
        <p className="text-xs text-gray-400 mb-5">
          {chartView.questionnaire.response_count ?? 0} response{(chartView.questionnaire.response_count ?? 0) !== 1 ? 's' : ''} ·{' '}
          {FREQ_LABELS[chartView.questionnaire.frequency as Frequency]}
        </p>
        <div className="space-y-6">
          {chartView.questions.map(q => (
            <div key={q.id}>
              <div className="flex items-center gap-2 mb-2">
                <TypeBadge type={q.type as QuestionType} />
                <p className="text-sm text-gray-300">{q.text}</p>
              </div>
              <QuestionChart q={q} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-white">Check-ins</h2>
          <p className="text-xs text-gray-500 mt-0.5">{questionnaires.length} active check-in{questionnaires.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setShowBuilder(true)}
          className="flex items-center gap-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
        >
          <PlusIcon className="w-4 h-4" /> New Check-in
        </button>
      </div>

      {loading ? (
        <Spinner size="md" className="py-10" />
      ) : questionnaires.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-10 h-10 rounded-full bg-dark-hover flex items-center justify-center mb-3">
            <ClipboardDocumentCheckIcon className="w-5 h-5 text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-400">No check-ins yet</p>
          <p className="text-xs text-gray-600 mt-1">Create one to track this client's progress over time</p>
          <button
            onClick={() => setShowBuilder(true)}
            className="mt-4 text-xs text-primary-400 hover:text-primary-300 hover:underline"
          >
            Create check-in
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {questionnaires.map(q => (
            <div
              key={q.id}
              className="flex items-center justify-between px-4 py-3 bg-dark-hover border border-dark-border rounded-xl hover:border-gray-600 transition-colors"
            >
              <button className="flex-1 text-left min-w-0" onClick={() => openCharts(q)}>
                <p className="text-sm font-medium text-white truncate">{q.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {FREQ_LABELS[q.frequency as Frequency]} ·{' '}
                  {q.response_count ?? 0} response{(q.response_count ?? 0) !== 1 ? 's' : ''} ·{' '}
                  {q.next_due_date ? `Due ${q.next_due_date}` : 'No due date'}
                </p>
              </button>
              <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                <button
                  onClick={() => toggleActive(q)}
                  title={q.is_active ? 'Active — click to pause' : 'Paused — click to activate'}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${q.is_active ? 'bg-primary-600' : 'bg-gray-600'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${q.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
                </button>
                <button
                  onClick={() => sendReminder(q.id)}
                  title="Send reminder"
                  className="text-gray-500 hover:text-primary-400 transition-colors"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteQuestionnaire(q.id)}
                  title="Delete"
                  className="text-gray-500 hover:text-red-400 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {chartLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <Spinner size="lg" />
        </div>
      )}

      {showBuilder && (
        <QuestionnaireBuilder
          clientId={clientId}
          coachClientId={coachClientId}
          onSaved={() => { setShowBuilder(false); fetchQuestionnaires(); }}
          onClose={() => setShowBuilder(false)}
        />
      )}
    </div>
  );
};

// ─── Questionnaire Builder Modal ──────────────────────────────────────────────

const QuestionnaireBuilder: React.FC<{
  clientId: string;
  coachClientId?: string;
  onSaved: () => void;
  onClose: () => void;
}> = ({ clientId, coachClientId, onSaved, onClose }) => {
  const [title, setTitle] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('weekly');
  const [occurrences, setOccurrences] = useState<string>('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [questions, setQuestions] = useState<DraftQuestion[]>([]);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const generate = async () => {
    if (!coachClientId) {
      setGenerateError('Cannot generate: coach–client relationship ID not available.');
      return;
    }
    setGenerating(true);
    setGenerateError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.generate}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ coach_client_id: Number(coachClientId) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generation failed');
      if (data.questions?.length) {
        setQuestions(data.questions.map((q: GeneratedQuestion) => ({ text: q.text, type: q.type })));
        if (!title) setTitle('Weekly Check-in');
      } else {
        setGenerateError(data.message || 'No questions returned — add them manually.');
      }
    } catch (e: any) {
      setGenerateError(e.message || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const addQuestion = () => {
    if (questions.length >= 10) return;
    setQuestions(q => [...q, { text: '', type: 'rating' }]);
  };

  const updateQuestion = (i: number, field: keyof DraftQuestion, value: string) => {
    setQuestions(q => q.map((item, idx) => idx === i ? { ...item, [field]: value } : item));
  };

  const removeQuestion = (i: number) => setQuestions(q => q.filter((_, idx) => idx !== i));

  const moveUp = (i: number) => {
    if (i === 0) return;
    setQuestions(q => { const a = [...q]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; return a; });
  };

  const moveDown = (i: number) => {
    if (i === questions.length - 1) return;
    setQuestions(q => { const a = [...q]; [a[i], a[i + 1]] = [a[i + 1], a[i]]; return a; });
  };

  const save = async () => {
    if (!title.trim()) { setSaveError('Please enter a title'); return; }
    if (frequency !== 'once' && (!occurrences || Number(occurrences) < 1)) {
      setSaveError('Please enter the number of occurrences'); return;
    }
    if (questions.length === 0) { setSaveError('Add at least one question'); return; }
    const invalid = questions.find(q => !q.text.trim());
    if (invalid) { setSaveError('All question texts must be filled in'); return; }

    setSaving(true);
    setSaveError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}${endpoints.main.questionnaires.create}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          coach_client_id: coachClientId ? Number(coachClientId) : undefined,
          title: title.trim(),
          frequency,
          next_due_date: startDate,
          occurrences: frequency !== 'once' && occurrences !== '' ? Number(occurrences) : undefined,
          questions: questions.map((q, i) => ({ text: q.text, type: q.type, order_index: i })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      onSaved();
    } catch (e: any) {
      setSaveError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-xl shadow-2xl border flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
          <h3 className="font-semibold text-gray-900">New Check-in</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">

          {/* Title + Frequency + Start date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Weekly Check-in"
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
              <select
                value={frequency}
                onChange={e => setFrequency(e.target.value as Frequency)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
              >
                {(Object.entries(FREQ_LABELS) as [Frequency, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First due date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {frequency !== 'once' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Occurrences <span className="text-gray-400 font-normal">(max 12)</span> <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={12}
                  required
                  value={occurrences}
                  onChange={e => setOccurrences(e.target.value)}
                  placeholder="Required"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}
          </div>

          {/* Generate button */}
          <div className="border border-dashed border-gray-300 rounded-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium text-gray-700">Questions</p>
              <button
                onClick={generate}
                disabled={generating}
                className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
              >
                <SparklesIcon className="w-4 h-4" />
                {generating ? 'Generating…' : 'Generate from Session Notes'}
              </button>
            </div>
            {generateError && <p className="text-xs text-amber-600 mt-1">{generateError}</p>}
            <p className="text-xs text-gray-400">
              Or add questions manually below. Max 10 questions.
            </p>
          </div>

          {/* Question list */}
          {questions.length > 0 && (
            <div className="space-y-3">
              {questions.map((q, i) => (
                <div key={i} className="flex gap-2 items-start">
                  {/* Up/Down */}
                  <div className="flex flex-col gap-0.5 pt-2 flex-shrink-0">
                    <button onClick={() => moveUp(i)} disabled={i === 0} className="text-gray-300 hover:text-gray-500 disabled:opacity-0">
                      <ChevronUpIcon className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => moveDown(i)} disabled={i === questions.length - 1} className="text-gray-300 hover:text-gray-500 disabled:opacity-0">
                      <ChevronDownIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {/* Text */}
                  <textarea
                    value={q.text}
                    onChange={e => updateQuestion(i, 'text', e.target.value)}
                    placeholder={`Question ${i + 1}`}
                    rows={2}
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                  {/* Type */}
                  <select
                    value={q.type}
                    onChange={e => updateQuestion(i, 'type', e.target.value)}
                    className="text-xs border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-primary-500 flex-shrink-0"
                  >
                    <option value="rating">1–5 Rating</option>
                    <option value="yesno">Yes / No</option>
                    <option value="freetext">Free text</option>
                    <option value="number">Number</option>
                  </select>
                  {/* Delete */}
                  <button onClick={() => removeQuestion(i)} className="text-gray-300 hover:text-red-400 pt-2 flex-shrink-0">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={addQuestion}
            disabled={questions.length >= 10}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40"
          >
            <PlusIcon className="w-4 h-4" />
            Add question {questions.length > 0 && `(${questions.length}/10)`}
          </button>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t bg-gray-50 flex items-center justify-between flex-shrink-0">
          {saveError ? <p className="text-xs text-red-600">{saveError}</p> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-white">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save & Activate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionnairesTab;
